import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { SEND_CHANNEL_LABEL, type DocumentSendChannelKey } from '@fieldops/shared'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { DialogFooter, DialogHeader, Modal } from '../Modal.js'
import { Button, ErrorState, Field, Input, Select, Textarea } from '../ui.js'

/**
 * Recording a copy that went out some way other than email.
 *
 * Without this the send history is a history of emails, and the card claims
 * "not sent" about a quote the customer has been holding since Tuesday — then
 * the office learns not to trust the words on the screen, which is worse than
 * not having them.
 *
 * It still renders and files a PDF, so a posted copy carries the same evidence
 * as an emailed one: version, date, sender, and the exact bytes that went.
 *
 * Email is deliberately not one of the channels. An email send writes its own
 * row, with a real message behind it; typing one in here would assert a
 * message that does not exist.
 */
const CHANNELS: DocumentSendChannelKey[] = ['POST', 'HANDED_OVER', 'OTHER']

export function MarkSentPopup({
  kind,
  id,
  number,
  open,
  onClose,
}: {
  kind: 'quote' | 'invoice'
  id: string
  number: string
  open: boolean
  onClose: () => void
}) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="mark-sent-title" className="max-w-[460px]">
      {open ? <Body kind={kind} id={id} number={number} onClose={onClose} /> : null}
    </Modal>
  )
}

function Body({
  kind,
  id,
  number,
  onClose,
}: {
  kind: 'quote' | 'invoice'
  id: string
  number: string
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const [channel, setChannel] = useState<DocumentSendChannelKey>('HANDED_OVER')
  const [toName, setToName] = useState('')
  const [note, setNote] = useState('')

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.quotes.pathFilter())
    void queryClient.invalidateQueries(trpc.invoices.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  const options = {
    onSuccess: () => {
      invalidate()
      onClose()
    },
  }
  const markQuote = useMutation(trpc.quotes.markSent.mutationOptions(options))
  const markInvoice = useMutation(trpc.invoices.markSent.mutationOptions(options))
  const mark = kind === 'quote' ? markQuote : markInvoice

  return (
    <>
      <DialogHeader
        id="mark-sent-title"
        title="Record it as sent"
        aside={<span className="font-mono">{number}</span>}
        onClose={onClose}
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        <p className="text-[13px] text-ink-600">
          For a copy that did not go by email. A PDF of the {kind} as it stands
          now is filed against the job, so there is a record of exactly what
          the customer was given.
        </p>

        <Field label="How it went" htmlFor="mark-sent-channel">
          <Select
            id="mark-sent-channel"
            value={channel}
            onChange={(event) => setChannel(event.target.value as DocumentSendChannelKey)}
          >
            {CHANNELS.map((key) => (
              <option key={key} value={key}>
                {SEND_CHANNEL_LABEL[key]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Who got it" hint="Optional" htmlFor="mark-sent-to">
          <Input
            id="mark-sent-to"
            value={toName}
            onChange={(event) => setToName(event.target.value)}
            placeholder="Left with Trish on site"
          />
        </Field>

        <Field label="Note" hint="Optional" htmlFor="mark-sent-note">
          <Textarea
            id="mark-sent-note"
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Anything worth knowing about this copy."
          />
        </Field>

        {mark.error ? <ErrorState message={errorMessage(mark.error)} /> : null}
      </div>

      <DialogFooter>
        <Button size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          loading={mark.isPending}
          onClick={() =>
            mark.mutate({
              id,
              channel: channel as 'POST' | 'HANDED_OVER' | 'OTHER',
              toName: toName.trim() === '' ? null : toName.trim(),
              note: note.trim() === '' ? null : note.trim(),
            })
          }
        >
          Record it
        </Button>
      </DialogFooter>
    </>
  )
}
